import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { patients as patientsApi } from '@/api/endpoints'
import type { ApiError } from '@/api/errors'
import type { Paginated, SearchHitResponse } from '@/api/types'
import { Banner } from '@/components/Banner'
import { Card } from '@/components/Card'
import { EmptyState } from '@/components/EmptyState'
import { Icon } from '@/components/Icon'
import { Page, PageHeader } from '@/components/Page'
import { SkeletonList } from '@/components/Skeleton'
import { Spinner } from '@/components/Spinner'
import { useInfiniteScroll } from '@/lib/useInfiniteScroll'
import { PatientResultRow } from './PatientResultRow'
import { Button } from '@/components/Button'
import { useModals } from '@/app/modals/ModalContext'
import { errorMessage } from '@/api/messages'

const PAGE = 25
const MIN_QUERY = 2

/**
 * The register is thousands of rows, so it pages as you scroll, and the filter
 * lives in the list header rather than in a dropdown over it. Only the list
 * scrolls: the title, the action and the filter stay put.
 */
export function SearchScreen() {
  const modals = useModals()
  const [typed, setTyped] = useState('')
  const [query, setQuery] = useState('')
  const sentinel = useRef<HTMLDivElement>(null)

  // Debounced: a request per keystroke would trip the API's own search limiter,
  // which would be the limiter working correctly and the client being wrong.
  useEffect(() => {
    const id = setTimeout(() => setQuery(typed.trim()), 250)
    return () => clearTimeout(id)
  }, [typed])

  const searching = query.length >= MIN_QUERY

  const list = useInfiniteQuery<Paginated<SearchHitResponse>, ApiError>({
    queryKey: ['patients', 'list', searching ? query : ''],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      searching
        ? patientsApi.search(query, PAGE, pageParam as string | null, signal)
        : patientsApi.recent(PAGE, pageParam as string | null, signal),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  })

  const rows = list.data?.pages.flatMap((page) => page.data) ?? []

  useInfiniteScroll(sentinel, {
    hasMore: Boolean(list.hasNextPage),
    loading: list.isFetchingNextPage,
    onLoadMore: () => void list.fetchNextPage(),
  })

  return (
    <Page fill>
      <PageHeader
        eyebrow="Clinic"
        title="Patients"
        description="Everyone on the register, most recently seen first."
        actions={
          <Button onClick={() => modals.open({ kind: 'patient', mode: 'create' })}>
            <Icon name="userPlus" />
            Register new patient
          </Button>
        }
      />

      {list.error && (
        <div className="mb-4">
          <Banner tone={list.error.isNetwork ? 'warning' : 'danger'}>{errorMessage(list.error)}</Banner>
        </div>
      )}

      <Card
        title={searching ? 'Search results' : 'Recently seen'}
        action={
          <label className="flex h-field w-full max-w-88 flex-1 basis-72 items-center gap-2 rounded-md border border-control bg-surface px-3 text-fg-subtle transition duration-150 ease-standard hover:border-fg-subtle focus-within:border-accent focus-within:ring-3 focus-within:ring-focus/20 [&_input]:min-w-0 [&_input]:flex-1 [&_input]:self-stretch [&_input]:border-0 [&_input]:bg-transparent [&_input]:text-fg [&_input]:outline-none [&_input]:placeholder:text-fg-subtle">
            <span className="sr-only">Filter patients by OP number, name or phone</span>
            <Icon name="search" size="1rem" />
            <input
              type="search"
              value={typed}
              placeholder="Filter by OP No, name, phone…"
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
            />
          </label>
        }
        flush
        fill
      >
        {list.isLoading ? (
          <div className="p-5">
            <SkeletonList rows={6} label="Loading patients" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name={searching ? 'search' : 'userPlus'} size="2rem" />}
            title={searching ? 'No patient matches that' : 'No patients registered yet'}
            body={
              searching
                ? 'Try the OP number, the number they call from, or part of the name.'
                : 'Register the first patient to start their record.'
            }
          />
        ) : (
          <>
            <ul className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-soft">
              {rows.map((hit) => (
                <PatientResultRow key={hit.id} hit={hit} />
              ))}
            </ul>

            {/* Watched, not clicked: crossing it loads the next page. */}
            <div ref={sentinel} className="flex min-h-10 items-center justify-center p-3">
              {list.isFetchingNextPage && <Spinner label="Loading more patients" />}
              {!list.hasNextPage && rows.length > PAGE && (
                <p className="text-xs text-fg-subtle">That is everyone on the register.</p>
              )}
            </div>
          </>
        )}
      </Card>
    </Page>
  )
}
